import React from 'react';
import type { AxiosRequestConfig } from 'axios';
import i18next from 'i18next';

import type {
  ActivitiesApiActivitiesNotificationsListRequest,
  Notification,
  NotificationStatistics,
} from 'src/api-sdk';
import {
  cleanNotificationText,
  escapeRegExp,
  removeUnresolvedTemplateFields,
} from 'src/util/notificationFormatting';
import {
  DEFAULT_NOTIFICATION_LABEL,
  PROGRESS_NOTIFICATION_TYPES,
  getNotificationTypeLabel,
  isNotificationCenterNotificationType,
  knownNotificationTypes,
  notificationTypesByTabLabel,
  type NotificationCategory,
  type NotificationType,
  type NotificationTemplateData,
} from 'src/util/notificationConfig';

type NotificationListRequest = Omit<
  ActivitiesApiActivitiesNotificationsListRequest,
  'notificationType'
> & {
  notificationType?: NotificationType | NotificationType[];
  query?: string;
};

export type InboxNotificationItem = {
  id: string;
  notificationType: NotificationType;
  createdAt: string;
  updatedAt?: string;
  confirmedAt?: string | null;
  isUpdated: boolean;
  isUpdatedSinceRead: boolean;
  triggerType: NotificationType;
  typeLabel: string;
  priority: 'normal' | 'high';
  message: React.ReactNode;
  targetUrl?: string | null;
  quoteText?: string;
  quoteHighlightValues?: string[];
  progress?: number;
  progressDetail?: string;
  detailCardLabel?: string;
  detailCardVariant?: 'expired' | 'compliance-check';
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  companyName?: string;
  binderName?: string;
  articleName?: string;
  exportFileType?: string;
  iconUrl?: string;
};

export type FetchMappedNotificationsResult = {
  items: InboxNotificationItem[];
  confirmedIds: string[];
  nextKey?: string;
  totalResults?: number | null;
};

export type FetchNotificationUnreadCountsResult = {
  byType: Partial<Record<NotificationType, number>>;
  byCategory: Record<NotificationCategory | 'All', number>;
};

const meaningfulUpdatedAtById = new Map<string, string>();

export const clearNotificationCache = () => meaningfulUpdatedAtById.clear();

export const rememberNotificationMeaningfulUpdatedAt = (
  item: InboxNotificationItem
) => {
  if (
    isProgressNotificationType(item.notificationType) ||
    !item.isUpdated ||
    !item.updatedAt ||
    item.updatedAt === item.createdAt
  ) {
    return;
  }

  const current = meaningfulUpdatedAtById.get(item.id);
  if (
    !current ||
    new Date(item.updatedAt).getTime() > new Date(current).getTime()
  ) {
    meaningfulUpdatedAtById.set(item.id, item.updatedAt);
  }
};

export const applyNotificationMeaningfulUpdatedAt = (
  item: InboxNotificationItem
): InboxNotificationItem => {
  if (isProgressNotificationType(item.notificationType)) {
    return item;
  }

  const meaningfulUpdatedAt = meaningfulUpdatedAtById.get(item.id);
  if (
    !meaningfulUpdatedAt ||
    item.updatedAt !== item.createdAt ||
    new Date(meaningfulUpdatedAt).getTime() <=
      new Date(item.createdAt).getTime()
  ) {
    return item;
  }

  const confirmedAtMs = item.confirmedAt
    ? new Date(item.confirmedAt).getTime()
    : null;
  const updatedAtMs = new Date(meaningfulUpdatedAt).getTime();

  return {
    ...item,
    updatedAt: meaningfulUpdatedAt,
    isUpdated: true,
    isUpdatedSinceRead:
      Boolean(confirmedAtMs) &&
      confirmedAtMs !== null &&
      updatedAtMs > confirmedAtMs,
  };
};

// Return cleaned values from any backend data field that appears in the resolved
// text. This keeps highlighting data-driven: if a future template adds
// {some_new_key}, the value is picked up without a per-notification mapping.
const getTemplateValues = (
  resolvedText: string | null | undefined,
  data?: NotificationTemplateData | null
): string[] => {
  if (!data || !resolvedText) return [];
  const lowerText = resolvedText.toLowerCase();
  return Array.from(
    new Set(
      (Object.values(data) as unknown[])
        .map((v) =>
          removeUnresolvedTemplateFields(cleanNotificationText(String(v ?? '')))
        )
        .filter((value): value is string => {
          if (!value || value.length < 2) return false;
          return lowerText.includes(value.toLowerCase());
        })
    )
  );
};

// Resolve highlight values for the quote/detail line.
const getQuoteHighlightValues = (
  quoteText: string | null | undefined,
  data?: NotificationTemplateData | null
): string[] => getTemplateValues(quoteText, data);

// Highlight known values first. If none exist, highlight quoted text.
export const renderNotificationTitle = (
  text: string,
  data?: NotificationTemplateData | null,
  priority: 'normal' | 'high' = 'normal'
): React.ReactNode => {
  if (!text) {
    return text;
  }

  const highlightedClass =
    priority === 'high'
      ? 'font-medium text-[#B8241A]'
      : 'font-medium text-brand';

  const highlightValues = getTemplateValues(text, data);

  if (highlightValues.length > 0) {
    const sortedHighlightValues = [...highlightValues].sort(
      (a, b) => b.length - a.length
    );
    const tokenPattern = new RegExp(
      `(${sortedHighlightValues.map(escapeRegExp).join('|')})`,
      'i'
    );
    const highlightSet = new Set(
      sortedHighlightValues.map((v) => v.toLowerCase())
    );

    const parts = text.split(tokenPattern);
    return parts.map((part, index) =>
      highlightSet.has(part.toLowerCase()) ? (
        <span key={`${part}-${index}`} className={highlightedClass}>
          {part}
        </span>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    );
  }

  const quoteMatch = text.match(/["“]([^"”]+)["”]/);
  if (quoteMatch && typeof quoteMatch.index === 'number') {
    const before = text.slice(0, quoteMatch.index);
    const after = text.slice(quoteMatch.index + quoteMatch[0].length);

    return (
      <>
        {before}
        <span className={highlightedClass}>{quoteMatch[1]}</span>
        {after}
      </>
    );
  }

  return text;
};

const getPrimaryActionLabel = (
  notificationType: NotificationType,
  typeLabel: string,
  targetUrl?: string | null
) => {
  const t = i18next.t.bind(i18next);

  // Without a target there is nowhere specific to go, so no primary button is
  // shown — tapping the notification itself opens the inbox instead.
  if (!targetUrl) {
    return undefined;
  }

  switch (notificationType) {
    case 'BINDER_COMMENT':
    case 'BINDER_COMMENT_MENTION':
      return t('Go to Binder', { ns: 'inbox' });
    case 'NEWS_ARTICLE_FEEDBACK_RECEIVED':
      return t('Go to Article', { ns: 'inbox' });
    case 'COMMENT':
    case 'COMMENT_MENTION':
    case 'COMPANY_REGISTRATION_UPDATE':
    case 'COMPLIANCE_CHECK_UPDATE':
    case 'COMPANY_FEEDBACK_RECEIVED':
    case 'MISSING_COMPANY_INVESTIGATION_UPDATE':
    case 'BATCH_REGISTRATION_UPDATE':
    case 'BATCH_COMPLIANCE_CHECK_UPDATE':
      return t('Go to Company', { ns: 'inbox' });
    case 'SUPPORT_REQUEST_UPDATE':
      return t('Open support request', { ns: 'inbox' });
    default:
      break;
  }

  switch (typeLabel) {
    case 'Binders':
    case 'Mentions':
      return t('Go to Binder', { ns: 'inbox' });
    case 'Articles':
      return t('Go to Article', { ns: 'inbox' });
    case 'Companies':
      return t('Go to Company', { ns: 'inbox' });
    default:
      return t('Open', { ns: 'inbox' });
  }
};

const getDetailCardLabel = (
  notificationType: NotificationType,
  data?: NotificationTemplateData | null,
  targetUrl?: string | null,
  titleText?: string
) => {
  if (!targetUrl && notificationType !== 'EXPORT_UPDATE') {
    return undefined;
  }

  const t = i18next.t.bind(i18next);

  switch (notificationType) {
    case 'BINDER_COMMENT':
    case 'BINDER_COMMENT_MENTION':
      return (
        cleanNotificationText(data?.binder_name) || t('Binder', { ns: 'inbox' })
      );
    case 'NEWS_ARTICLE_FEEDBACK_RECEIVED':
      return (
        cleanNotificationText(data?.article_name) ||
        t('Article', { ns: 'inbox' })
      );
    case 'EXPORT_UPDATE':
      // The expired card relies on the same fallback chain; an expired export
      // can be missing export_type/file_type yet still needs a label so the
      // card stays visible.
      return (
        getExportDetailLabel(data) ||
        cleanNotificationText(data?.exportName) ||
        cleanNotificationText(data?.export_name) ||
        cleanNotificationText(data?.fileName) ||
        cleanNotificationText(data?.file_name) ||
        cleanNotificationText(data?.filename) ||
        cleanNotificationText(data?.name) ||
        getExportDetailFallback(titleText, targetUrl) ||
        t('Export file', { ns: 'inbox' })
      );
    case 'COMPLIANCE_CHECK_UPDATE':
      // A compliance check can run without a company; fall back to the check
      // name so the card is not mislabelled "Company".
      return (
        cleanNotificationText(data?.company_name) ||
        cleanNotificationText(data?.compliance_check_name) ||
        t('Company', { ns: 'inbox' })
      );
    case 'COMMENT':
    case 'COMMENT_MENTION':
    case 'COMPANY_REGISTRATION_UPDATE':
    case 'COMPANY_FEEDBACK_RECEIVED':
    case 'MISSING_COMPANY_INVESTIGATION_UPDATE':
      return (
        cleanNotificationText(data?.company_name) ||
        t('Company', { ns: 'inbox' })
      );
    case 'BATCH_REGISTRATION_UPDATE':
      return (
        cleanNotificationText(data?.batch_name) ||
        t('Batch registration', { ns: 'inbox' })
      );
    case 'BATCH_COMPLIANCE_CHECK_UPDATE':
      return (
        cleanNotificationText(data?.batch_name) ||
        cleanNotificationText(data?.company_name) ||
        t('Batch compliance check', { ns: 'inbox' })
      );
    case 'SUPPORT_REQUEST_UPDATE':
      return (
        cleanNotificationText(data?.support_request_subject) ||
        t('Support request', { ns: 'inbox' })
      );
    default:
      return undefined;
  }
};

const getExportDetailFallback = (
  titleText?: string,
  targetUrl?: string | null
) => {
  const titleMatch = titleText?.match(
    /^your\s+(.+?)\s+export\s+(?:is\s+)?(?:ready|being\s+processed)$/i
  );
  if (titleMatch?.[1]) {
    return cleanNotificationText(titleMatch[1]);
  }

  if (!targetUrl) {
    return undefined;
  }

  try {
    const url = new URL(targetUrl, 'https://businessradar.local');
    const filename = decodeURIComponent(url.pathname.split('/').pop() ?? '');
    return cleanNotificationText(filename);
  } catch {
    return undefined;
  }
};

const getExportDetailLabel = (data?: NotificationTemplateData | null) => {
  const exportType = cleanNotificationText(data?.export_type);
  const fileType = cleanNotificationText(data?.file_type);

  if (!exportType && !fileType) {
    return undefined;
  }

  const t = i18next.t.bind(i18next);
  const label = exportType
    ? `${exportType} ${t('Export', { ns: 'inbox' })}`
    : t('Export', { ns: 'inbox' });

  return fileType ? `${label} - ${fileType}` : label;
};

const normalizeProgress = (progress?: number | null) => {
  if (typeof progress !== 'number') {
    return undefined;
  }

  const percentage = progress <= 1 ? progress * 100 : progress;
  return Math.max(0, Math.min(100, percentage));
};

export const isProgressNotificationType = (
  notificationType: NotificationType
) => PROGRESS_NOTIFICATION_TYPES.includes(notificationType);

export const mapApiNotificationItem = (
  notification: Notification,
  defaultTypeLabel: string = DEFAULT_NOTIFICATION_LABEL,
  options: { rememberMeaningfulUpdate?: boolean } = {}
): InboxNotificationItem => {
  const notificationType = notification.notification_type as NotificationType;
  const templateData = notification.data as NotificationTemplateData | null;
  const titleText = cleanNotificationText(notification.title);
  const messageText = cleanNotificationText(notification.message);
  const MENTION_TYPES: NotificationType[] = [
    'COMMENT_MENTION',
    'BINDER_COMMENT_MENTION',
  ];
  const normalizedPriority: 'normal' | 'high' =
    notification.priority === 'HIGH' || MENTION_TYPES.includes(notificationType)
      ? 'high'
      : 'normal';

  const progressStatusText =
    cleanNotificationText(templateData?.status_display) ||
    cleanNotificationText(notification.status || '');
  const normalizedProgress = normalizeProgress(notification.progress);
  const hasProgress =
    isProgressNotificationType(notificationType) &&
    normalizedProgress !== undefined &&
    normalizedProgress > 0 &&
    normalizedProgress < 100;

  // Prefer comment text in the quote bubble.
  const quoteText =
    cleanNotificationText(templateData?.comment_text) ||
    (messageText && messageText !== titleText ? messageText : undefined);

  const rawQuoteText = (notification.data as Record<string, unknown> | null)
    ?.comment_text as string | undefined;
  const quoteHighlightValues = getQuoteHighlightValues(
    rawQuoteText ?? notification.message,
    templateData
  ).filter((value) => value !== quoteText);

  const createdAt = notification.created_at;
  const updatedAt = notification.updated_at || notification.created_at;
  const confirmedAt = notification.confirmed_at || null;
  const createdAtMs = new Date(createdAt).getTime();
  const updatedAtMs = new Date(updatedAt).getTime();
  const confirmedAtMs = confirmedAt ? new Date(confirmedAt).getTime() : null;
  const isUpdated = updatedAtMs > createdAtMs;
  const isUpdatedSinceRead =
    Boolean(confirmedAtMs) &&
    confirmedAtMs !== null &&
    updatedAtMs > confirmedAtMs;
  const typeLabel = getNotificationTypeLabel(
    notificationType,
    defaultTypeLabel
  );
  const exportFileType = cleanNotificationText(templateData?.file_type);
  const targetUrl =
    notificationType === 'EXPORT_UPDATE'
      ? cleanNotificationText(templateData?.download_url) ||
        notification.target_url
      : notification.target_url;
  // The export detail card is only meaningful once the export has completed
  // (variant 'complete'): completed exports either link to the file or, if the
  // link has lapsed, show the expired state. While an export is still being
  // processed (variant 'in_progress') no card is shown.
  const isExportComplete =
    notificationType === 'EXPORT_UPDATE' &&
    cleanNotificationText(templateData?.variant) === 'complete';
  const isExportLinkExpired = isExportComplete && !targetUrl;
  const detailCardTargetUrl =
    notificationType === 'EXPORT_UPDATE'
      ? targetUrl
      : hasProgress
        ? null
        : targetUrl;

  const item: InboxNotificationItem = {
    id: notification.external_id,
    notificationType,
    createdAt,
    updatedAt,
    confirmedAt,
    isUpdated,
    isUpdatedSinceRead,
    triggerType: notificationType,
    typeLabel,
    priority: normalizedPriority,
    message: (
      <span>
        {renderNotificationTitle(titleText, templateData, normalizedPriority)}
      </span>
    ),
    targetUrl,
    quoteText,
    quoteHighlightValues,
    progress: hasProgress ? normalizedProgress : undefined,
    progressDetail: hasProgress ? progressStatusText : undefined,
    detailCardLabel:
      notificationType === 'EXPORT_UPDATE' && !isExportComplete
        ? undefined
        : getDetailCardLabel(
            notificationType,
            templateData,
            detailCardTargetUrl,
            titleText
          ),
    detailCardVariant: isExportLinkExpired
      ? 'expired'
      : notificationType === 'COMPLIANCE_CHECK_UPDATE' &&
          !cleanNotificationText(templateData?.company_name) &&
          cleanNotificationText(templateData?.compliance_check_name)
        ? 'compliance-check'
        : undefined,
    primaryActionLabel: getPrimaryActionLabel(
      notificationType,
      typeLabel,
      targetUrl
    ),
    companyName: cleanNotificationText(templateData?.company_name) || undefined,
    binderName: cleanNotificationText(templateData?.binder_name) || undefined,
    articleName: cleanNotificationText(templateData?.article_name) || undefined,
    exportFileType: exportFileType || undefined,
    iconUrl: templateData?.icon_url || undefined,
  };

  if (options.rememberMeaningfulUpdate !== false) {
    rememberNotificationMeaningfulUpdatedAt(item);
  }

  return applyNotificationMeaningfulUpdatedAt(item);
};

type UnreadCountsParams = {
  priority?: 'HIGH';
  minCreatedAt?: string;
  maxCreatedAt?: string;
  confirmed?: 'false' | 'true';
};

// Fetch notifications and return ids that are already read.
export const fetchMappedNotifications = async (
  api:
    | {
        activitiesAPI?: {
          activitiesNotificationsList: (
            requestParameters?: ActivitiesApiActivitiesNotificationsListRequest,
            options?: AxiosRequestConfig
          ) => Promise<{
            data?: {
              results?: Notification[];
              next_key?: string | null;
              total_results?: number | null;
            };
          }>;
          activitiesNotificationsStatisticsRetrieve?: (
            requestParameters?: UnreadCountsParams
          ) => Promise<{
            data: NotificationStatistics;
          }>;
        };
      }
    | null
    | undefined,
  defaultTypeLabel: string = DEFAULT_NOTIFICATION_LABEL,
  requestParameters?: NotificationListRequest
): Promise<FetchMappedNotificationsResult> => {
  if (!api?.activitiesAPI) {
    return { items: [], confirmedIds: [] };
  }

  const { query, notificationType, ...notificationRequestParameters } =
    requestParameters ?? {};
  const notificationTypes = Array.isArray(notificationType)
    ? notificationType
    : notificationType
      ? [notificationType]
      : undefined;

  if (notificationTypes?.length === 0) {
    return { items: [], confirmedIds: [], totalResults: 0 };
  }

  const request = {
    sorting: 'updated_at' as const,
    sortingOrder: 'descending' as const,
    ...notificationRequestParameters,
    ...(query ? { query } : {}),
    ...(notificationTypes ? { notificationType: notificationTypes } : {}),
  };
  const response = await api.activitiesAPI.activitiesNotificationsList(
    request as ActivitiesApiActivitiesNotificationsListRequest
  );

  const results = (response.data?.results ?? []).filter((notification) =>
    isNotificationCenterNotificationType(notification.notification_type)
  );
  const items = results.map((notification) =>
    mapApiNotificationItem(notification, defaultTypeLabel)
  );
  const confirmedIds = items
    .filter((notification) => Boolean(notification.confirmedAt))
    .filter((notification) => !notification.isUpdatedSinceRead)
    .map((notification) => notification.id);

  return {
    items,
    confirmedIds,
    nextKey: response.data?.next_key ?? undefined,
    totalResults: response.data?.total_results,
  };
};

export const fetchNotificationUnreadCounts = async (
  api: Parameters<typeof fetchMappedNotifications>[0],
  params?: UnreadCountsParams
): Promise<FetchNotificationUnreadCountsResult> => {
  const response =
    await api?.activitiesAPI?.activitiesNotificationsStatisticsRetrieve?.(
      params ?? {}
    );
  const counts = response?.data?.counts ?? {};
  const byType = knownNotificationTypes.reduce<
    Partial<Record<NotificationType, number>>
  >((acc, notificationType) => {
    acc[notificationType] = counts[notificationType] ?? 0;
    return acc;
  }, {});

  const byCategory = Object.entries(notificationTypesByTabLabel).reduce(
    (acc, [category, notificationTypes]) => {
      acc[category as NotificationCategory] = notificationTypes.reduce(
        (sum, notificationType) => sum + (byType[notificationType] ?? 0),
        0
      );
      return acc;
    },
    { All: 0 } as Record<NotificationCategory | 'All', number>
  );

  // Prefer the backend's authoritative total; fall back to summing known types
  // if it is not present.
  byCategory.All =
    response?.data?.total ??
    knownNotificationTypes.reduce(
      (sum, notificationType) => sum + (byType[notificationType] ?? 0),
      0
    );

  return { byType, byCategory };
};
